/**
 * Générateur — Automatisme : savoir qu'un angle droit mesure 90°, qu'un angle
 * plat mesure 180°.
 * Attendu ID : 1073 | Niveau : 5ème | Domaine : Angles
 * Rubrique : AUTOMATISME (programme 2025) — mobilisation en rituel court.
 * Labels (cartes exerciseur) :
 *   - Les mesures à connaître        (saisie flash ; QCM fractions du tour)
 *   - Sur la figure                  (saisie : plat, droit codé, droit déduit — la « partie
 *                                     d'un angle droit » de la question d. est hors attendu,
 *                                     retirée ; le QCM justification de la question c. retiré,
 *                                     question unique reconnue à sa forme, 2026-09-04)
 *   - Angle droit, angle plat : vrai ou faux ?  (V/F pool + deux lignes numériques)
 *
 * ── Plan de cartes validé (2026-09-03) ─────────────────────────────────────
 * Source : Automatisme n°35 du doc 5e + grille P1-P12 (analyse BO cycles 3-4).
 * P1 : angle droit INTRODUIT au CM (équerre), 90° CONSTATÉ au CM2 ; angle plat,
 * 180°, rapporteur et codage CONSOLIDÉS en 6e ; tout BASCULÉ en automatisme en 5e.
 * P2 : sens installés — droit = coin de l'équerre puis 90° ; plat = alignement
 * puis 180° ; tour complet 360°. Distracteurs sur les confusions : droit = 100°
 * (réflexe décimal), plat ⇄ plein, trois droits = un tour.
 * P3 : 0, 45, 90, 180, 270, 360 ; une soustraction à 90 ou à 180.
 * P5 : points alignés, perpendiculaire codée, demi-droite intérieure ; fractions
 * du tour (quart, moitié).
 * P6/P10 : pas de rapporteur ; l'angle droit se lit au petit carré, le plat à
 * l'alignement ; « justifie à partir de l'angle plat, sans rapporteur » (q. c).
 * P7 : les complémentaires ne sont pas nommés (hors liste cycle 3) : « il reste ».
 * P11 : plat 180 contre plein 360 ; « 100 » pour l'angle droit.
 * P12 : rémanence forte, pas d'item de réactivation.
 * INVARIANTS :
 *   - la figure est CALCULÉE (demi-droites en degrés entiers) et vérifiée : A,
 *     O, B alignés (180°), (OC) ⟂ (AB) (90°), D à l'intérieur de AOC ;
 *   - un seul angle droit est CODÉ (petit carré) ; l'autre se DÉDUIT de l'angle
 *     plat ; l'angle droit non codé n'est jamais demandé comme « codé » ;
 *   - data-rays et data-droit dans le SVG pour les solveurs ; réponses
 *     ENTIÈRES ; pas de calculatrice ;
 *   - V/F : fenêtre stricte des 4 dernières lignes fixes servies, ligne
 *     numérique jamais déductible d'une ligne fixe.
 */
import {
    randomChoice, randInt, shuffle,
    makeQcm, makeVraiFaux, makeSaisie,
} from '../../../utils';
import { imgElement } from '../statistiques/svgStats';

const ATTENDU_ID = 1073;
const opts = (label) => ({ niveau: '5eme', matiere: 'maths', attendu: ATTENDU_ID, label });

const L_MESURES = 'Les mesures à connaître';
const L_FIGURE = 'Sur la figure';
const L_VF = 'Angle droit, angle plat : vrai ou faux ?';

const mod360 = (x) => ((x % 360) + 360) % 360;

// ─── Carte 1a : les mesures à connaître (saisie flash) ─────────────────────

const QUESTIONS_MESURES = [
    { cle: 'droit', q: 'Un angle droit mesure …', r: 90 },
    { cle: 'plat', q: 'Un angle plat mesure …', r: 180 },
    // « nul » et « plein » retirés : faits de l'attendu n°34 (1072), pas des déductions de 90 / 180 (décision prof 2026-09-04).
    { cle: 'deux_droits', q: 'Deux angles droits accolés forment un angle qui mesure …', r: 180 },
    { cle: 'trois_droits', q: 'Trois angles droits accolés forment un angle qui mesure …', r: 270 },
    { cle: 'quatre_droits', q: 'Quatre angles droits accolés forment un angle qui mesure …', r: 360 },
    { cle: 'moitie_droit', q: 'La moitié d\'un angle droit mesure …', r: 45 },
    { cle: 'moitie_plat', q: 'La moitié d\'un angle plat mesure …', r: 90 },
];

let dernieresMesures1073 = [];

const genMesuresSaisie = () => {
    const dispo = QUESTIONS_MESURES.filter((q) => !dernieresMesures1073.includes(q.cle));
    const Q = randomChoice(dispo);
    dernieresMesures1073 = [...dernieresMesures1073, Q.cle].slice(-3);
    const payload = makeSaisie(String(Q.r), `<p>Complète : ${Q.q}</p>`, { ...opts(L_MESURES), suffixe: '°' });
    payload.contenu = { ...payload.contenu, sousType: Q.cle };
    payload._meta.params = { cle: Q.cle, reponse: Q.r };
    return payload;
};

// ─── Carte 1b : fractions du tour (QCM) ────────────────────────────────────

const FRACTIONS = ['un quart', 'la moitié', 'le tiers', 'les trois quarts'];
const NOMS_ANGLES = { droit: 'un angle droit', plat: 'un angle plat', plein: 'un angle plein', nul: 'un angle nul' };

const QUESTIONS_FRACTION = [
    { cle: 'droit_vers_fraction', texte: 'Un angle droit, c\'est quelle fraction du tour complet ?', bonne: 'un quart', options: FRACTIONS },
    { cle: 'plat_vers_fraction', texte: 'Un angle plat, c\'est quelle fraction du tour complet ?', bonne: 'la moitié', options: FRACTIONS },
    { cle: 'quart_vers_angle', texte: 'Un quart de tour, c\'est …', bonne: 'un angle droit', options: Object.values(NOMS_ANGLES) },
    { cle: 'demi_vers_angle', texte: 'Un demi-tour, c\'est …', bonne: 'un angle plat', options: Object.values(NOMS_ANGLES) },
    // « un tour complet, c'est un angle plein » retiré : nature d'angle (1072), décision prof 2026-09-04.
    // Deux questions déduites de 90 / 180 à la place (pool ≥ 5, seuil bloquant du harnais).
    { cle: 'trois_quarts_vers_mesure', texte: 'Trois quarts de tour, c\'est un angle qui mesure …', bonne: '270°', options: ['270°', '180°', '90°', '360°'] },
    { cle: 'deux_droits_vers_fraction', texte: 'Deux angles droits accolés, c\'est quelle fraction du tour complet ?', bonne: 'la moitié', options: FRACTIONS },
];

let dernieresFractions1073 = [];

const genFractionQcm = () => {
    const dispo = QUESTIONS_FRACTION.filter((q) => !dernieresFractions1073.includes(q.cle));
    const Q = randomChoice(dispo);
    dernieresFractions1073 = [...dernieresFractions1073, Q.cle].slice(-2);
    const payload = makeQcm([...Q.options], Q.bonne, `<p>${Q.texte}</p>`, opts(L_MESURES));
    payload._meta.params = { cle: Q.cle };
    return payload;
};

// ─── Carte 2 : sur la figure ───────────────────────────────────────────────
// Configuration de la fiche : A, O, B alignés ; (OC) ⟂ (AB) ; [OD) à
// l'intérieur de AOC, angle AOD = x. Rotation aléatoire, C d'un côté ou de l'autre.

const construireFigure = ({ avecD = false, x = null } = {}) => {
    const theta = randInt(0, 359);
    const sens = Math.random() < 0.5 ? 1 : -1;     // C au-dessus ou au-dessous de (AB)
    const rayons = { A: mod360(theta), B: mod360(theta + 180), C: mod360(theta + sens * 90) };
    let xD = null;
    if (avecD) {
        xD = x ?? randInt(20, 70);
        rayons.D = mod360(theta + sens * xD);
    }
    // Gardes : alignement et perpendicularité RECALCULÉES.
    if (mod360(rayons.B - rayons.A) !== 180) throw new Error('gen_1073 : A, O, B non alignés');
    if (mod360(rayons.C - rayons.A) !== 90 && mod360(rayons.A - rayons.C) !== 90) throw new Error('gen_1073 : (OC) non perpendiculaire');
    // Le petit carré est codé d'un côté : entre [OA) et [OC), ou entre [OC) et [OB).
    const codeCote = randomChoice(['AC', 'CB']);
    return { rayons, theta, sens, xD, codeCote };
};

const W = 320; const H = 240; const O = [160, 124]; const RAYON = 88;
const f1 = (v) => v.toFixed(1);
const pointSur = (deg, r) => [O[0] + r * Math.cos((deg * Math.PI) / 180), O[1] - r * Math.sin((deg * Math.PI) / 180)];
const secteurTrigo = (rayons, X, Y) => (mod360(rayons[Y] - rayons[X]) <= 180 ? [rayons[X], rayons[Y]] : [rayons[Y], rayons[X]]);

const svgArcOuvert = (a1, a2, r, attrs) => {
    const sweep = mod360(a2 - a1);
    if (sweep === 0) return '';
    const p1 = pointSur(a1, r); const p2 = pointSur(a2, r);
    return `<path d="M ${f1(p1[0])} ${f1(p1[1])} A ${r} ${r} 0 ${sweep > 180 ? 1 : 0} 0 ${f1(p2[0])} ${f1(p2[1])}" fill="none" ${attrs}/>`;
};

const svgCarre = (a1, a2) => {
    const k = 12; const p1 = pointSur(a1, k); const p3 = pointSur(a2, k);
    const p2 = [p1[0] + p3[0] - O[0], p1[1] + p3[1] - O[1]];
    return `<polyline points="${f1(p1[0])},${f1(p1[1])} ${f1(p2[0])},${f1(p2[1])} ${f1(p3[0])},${f1(p3[1])}" fill="none" stroke="#111827" stroke-width="1.8" data-code="carre"/>`;
};

/** Étiquette « O » dans le plus grand secteur libre (hors codage). */
const positionEtiquetteO = (rayons, occupes) => {
    const angles = Object.values(rayons).map(mod360).sort((a, b) => a - b);
    const dans = (deg, [a1, a2]) => mod360(deg - a1) <= mod360(a2 - a1);
    let meilleur = null; let ecartMax = -1;
    for (let k = 0; k < angles.length; k++) {
        const suivant = k + 1 < angles.length ? angles[k + 1] : angles[0] + 360;
        const ecart = suivant - angles[k]; const bis = mod360(angles[k] + ecart / 2);
        if (occupes.some((sec) => dans(bis, sec))) continue;
        if (ecart > ecartMax) { ecartMax = ecart; meilleur = bis; }
    }
    if (meilleur === null) meilleur = mod360(angles[0] + 45);
    const recul = Math.min(40, Math.max(16, 9 / Math.sin((Math.max(ecartMax, 10) * Math.PI) / 360)));
    return pointSur(meilleur, recul);
};

/**
 * options.valeurs : { 'AD': '55°', 'DC': '?' } ; options.arcs : [[X, Y], …]
 */
const svgFigure = (fig, options = {}) => {
    const { rayons, codeCote } = fig;
    let s = '';
    (options.arcs || []).forEach(([X, Y], i) => {
        const [a1, a2] = secteurTrigo(rayons, X, Y);
        s += svgArcOuvert(a1, a2, 22 + i * 6, 'stroke="#B45309" stroke-width="1.5"');
    });
    Object.entries(rayons).forEach(([nom, deg]) => {
        const P = pointSur(deg, RAYON);
        s += `<line x1="${f1(O[0])}" y1="${f1(O[1])}" x2="${f1(P[0])}" y2="${f1(P[1])}" stroke="#1E3A8A" stroke-width="2.2"/>`;
        const L = pointSur(deg, RAYON + 14);
        s += `<text x="${f1(L[0])}" y="${f1(L[1] + 5)}" font-size="13" font-weight="700" fill="#1e293b" text-anchor="middle">${nom}</text>`;
    });
    const [c1, c2] = secteurTrigo(rayons, codeCote[0], codeCote[1]);
    s += svgCarre(c1, c2);
    Object.entries(options.valeurs || {}).forEach(([cle, txt]) => {
        const [a1, a2] = secteurTrigo(rayons, cle[0], cle[1]);
        const sweep = mod360(a2 - a1); const bis = a1 + sweep / 2;
        const recul = Math.max(44, 12 / Math.tan((Math.max(sweep, 8) * Math.PI) / 360) + 8);
        const P = pointSur(bis, Math.min(recul, 78));
        s += `<text x="${f1(P[0])}" y="${f1(P[1] + 4)}" font-size="12.5" font-weight="700" fill="#B45309" text-anchor="middle">${txt}</text>`;
    });
    const lO = positionEtiquetteO(rayons, [[c1, c2]]);
    s += `<circle cx="${f1(O[0])}" cy="${f1(O[1])}" r="2.6" fill="#1e293b"/>`
        + `<text x="${f1(lO[0])}" y="${f1(lO[1] + 5)}" font-size="13" font-weight="700" fill="#1e293b" text-anchor="middle">O</text>`;
    const dataRays = Object.entries(rayons).map(([n, d]) => `${n}:${d}`).join('|');
    return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${W} ${H}" width="${W}" height="${H}" font-family="Arial" data-rays="${dataRays}" data-droit="${codeCote}">${s}</svg>`;
};

// Nom d'angle avec le chapeau sur le sommet : AÔB (accent circonflexe combinant, demande prof 2026-09-04).
const CHAPEAU = '\u0302';
const nomAngle = (X, Y) => `${X}O${CHAPEAU}${Y}`;

let derniereSignatureFigure1073 = null;

const genFigureSaisie = () => {
    let payload = construireFigureSaisie();
    for (let t = 0; t < 8 && payload._meta.params.signature === derniereSignatureFigure1073; t++) payload = construireFigureSaisie();
    derniereSignatureFigure1073 = payload._meta.params.signature;
    return payload;
};

const construireFigureSaisie = () => {
    // Sous-type « partie d'un angle droit » (question d. de la fiche, 90 − x) retiré :
    // hors attendu, c'est un calcul de reste, pas la connaissance de 90° (décision prof 2026-09-04).
    // Angle plat une fois sur deux (revue 2026-09-04, A2) : les réponses 90 et 180 à égalité.
    const sousType = Math.random() < 0.5 ? 'plat' : randomChoice(['droit_code', 'droit_deduit']);
    const fig = construireFigure({ avecD: false });
    const { codeCote } = fig;
    const codeAngle = codeCote === 'AC' ? ['A', 'C'] : ['C', 'B'];
    const autreAngle = codeCote === 'AC' ? ['C', 'B'] : ['A', 'C'];
    let texte; let reponse; let svg;
    const intro = '<p>Les points A, O et B sont alignés dans cet ordre, et la droite (OC) est perpendiculaire à (AB).</p>';
    if (sousType === 'plat') {
        texte = `${intro}<p>Quelle est la mesure de l'angle ${nomAngle('A', 'B')} ?</p>`;
        reponse = 180;
        svg = svgFigure(fig, { arcs: [['A', 'B']], valeurs: { AB: '?' } });
    } else if (sousType === 'droit_code') {
        texte = `${intro}<p>Quelle est la mesure de l'angle ${nomAngle(...codeAngle)} (codé) ?</p>`;
        reponse = 90;
        svg = svgFigure(fig, { valeurs: { [codeAngle.join('')]: '?' } });
    } else {
        texte = `${intro}<p>L'angle ${nomAngle(...codeAngle)} mesure 90°. Que vaut l'angle ${nomAngle(...autreAngle)} ?</p>`;
        reponse = 90;
        svg = svgFigure(fig, { arcs: [autreAngle], valeurs: { [codeAngle.join('')]: '90°', [autreAngle.join('')]: '?' } });
    }
    if (!Number.isInteger(reponse) || reponse < 0) throw new Error('gen_1073 : réponse non entière');
    const payload = makeSaisie(String(reponse), texte, {
        ...opts(L_FIGURE), suffixe: '°', elements_additionnels: imgElement(svg, 'medium'),
    });
    payload.contenu = { ...payload.contenu, sousType };
    payload._meta.params = { sousType, reponse, signature: `${sousType}-${reponse}-${fig.theta}` };
    return payload;
};

// Le QCM justification (question c.) a été retiré : une seule question, une seule
// bonne réponse, reconnue à sa forme dès la deuxième version (décision prof 2026-09-04).

// ─── Carte 3 : vrai ou faux ? ──────────────────────────────────────────────

const POOL_VF = [
    { l: 'Un angle droit mesure 90°.', v: true },
    { l: 'Un angle plat mesure 180°.', v: true },
    { l: 'Quatre angles droits accolés forment un tour complet.', v: true },
    { l: 'Un angle plat est le double d\'un angle droit.', v: true },   // remplace « deux droits accolés = plat », testée par la ligne numérique
    { l: 'Un angle droit est le quart d\'un tour complet.', v: true },
    { l: 'La moitié d\'un angle droit mesure 45°.', v: true },
    { l: 'Un angle droit mesure 100°.', v: false },
    { l: 'Un angle plat mesure 360°.', v: false },
    { l: 'Trois angles droits accolés forment un tour complet.', v: false },
    { l: 'Un angle plat est le tiers d\'un tour complet.', v: false },
    { l: 'Quatre angles droits accolés forment un angle plat.', v: false },
    { l: 'La moitié d\'un angle plat mesure 45°.', v: false },
];
if (POOL_VF.filter((it) => it.v).length !== 6) throw new Error('gen_1073 : pool V/F déséquilibré');

let derniersServisVf1073 = [];

const genVf = () => {
    for (let essai = 0; essai < 60; essai++) {
        // Deux lignes NUMÉRIQUES recalculées.
        // La VÉRITÉ de chaque ligne numérique est tirée à pile ou face, puis les valeurs
        // (revue 2026-09-04, A2) : les faux ne dominent plus les tableaux.
        const dit = randomChoice(['droit', 'plat']);
        const vraie = Math.random() < 0.5;
        const bonneMesure = dit === 'droit' ? 90 : 180;
        const m = vraie ? bonneMesure : randomChoice([45, 80, 100, 180, 270].filter((v) => v !== bonneMesure).concat(dit === 'plat' ? [90, 360] : []));
        // Seconde ligne numérique : deux angles droits accolés (décision prof 2026-09-04, la
        // ligne « partie d'un angle droit » est hors attendu).
        const vraie2 = Math.random() < 0.5;
        const somme = vraie2 ? 180 : randomChoice([90, 100, 200, 270, 360]);
        const numeriques = [
            { l: `Un angle de ${m}° est un angle ${dit}.`, v: vraie },
            { l: `Deux angles droits accolés mesurent ${somme}° à eux deux.`, v: vraie2 },
        ];
        if ((m === bonneMesure) !== vraie || (somme === 180) !== vraie2) throw new Error('gen_1073 : ligne numérique mal calibrée');
        // A8 : la ligne « un angle de m° est droit/plat » exclut les lignes de définition correspondantes.
        const exclues = dit === 'droit' ? ['Un angle droit mesure 90°.', 'Un angle droit mesure 100°.'] : ['Un angle plat mesure 180°.', 'Un angle plat mesure 360°.'];
        const dispo = POOL_VF.filter((it) => !derniersServisVf1073.includes(it.l) && !exclues.includes(it.l));
        const fixes = shuffle(dispo).slice(0, 2);
        const tirage = shuffle([...numeriques, ...fixes]);
        const nbFaux = tirage.filter((it) => !it.v).length;
        if (nbFaux < 1 || nbFaux > 3) continue;
        derniersServisVf1073 = [...derniersServisVf1073, ...fixes.map((it) => it.l)].slice(-4);
        const payload = makeVraiFaux(
            tirage.map((it) => it.l),
            tirage.map((it) => (it.v ? 0 : 1)),
            '<p>Pour chaque affirmation, dis si elle est vraie ou fausse.</p>',
            opts(L_VF),
        );
        payload.contenu = { ...payload.contenu, cadence: 'raisonnement' };
        return payload;
    }
    throw new Error('gen_1073 : V/F sans mixité après 60 essais');
};

// ─── Registre ──────────────────────────────────────────────────────────────

const GENERATORS = {
    mesures_saisie: genMesuresSaisie,
    mesures_fraction_qcm_unique: genFractionQcm,
    figure_saisie: genFigureSaisie,
    vf_tableau_vf: genVf,
};

const FORMAT_LABELS = { saisie: 'Saisie', fraction_qcm_unique: 'QCM fraction du tour', tableau_vf: 'Vrai/Faux' };

const buildFormats = (prefix) =>
    Object.keys(GENERATORS).filter((k) => k.startsWith(`${prefix}_`))
        .map((k) => ({ key: k, label: FORMAT_LABELS[k.slice(prefix.length + 1)] || k }));

export const CATEGORIES = [
    { id: 'mesures', label: L_MESURES, formatsList: buildFormats('mesures') },
    { id: 'figure', label: L_FIGURE, formatsList: buildFormats('figure') },
    { id: 'vf', label: L_VF, formatsList: buildFormats('vf') },
];

export const generate = (typeExercice) => {
    let key;
    if (typeExercice && GENERATORS[typeExercice]) key = typeExercice;
    else if (typeExercice) {
        const matching = Object.keys(GENERATORS).filter((k) => k.startsWith(typeExercice) || k.includes(typeExercice));
        key = matching.length ? randomChoice(matching) : randomChoice(Object.keys(GENERATORS));
    } else key = randomChoice(Object.keys(GENERATORS));
    const payload = GENERATORS[key]();
    payload._meta.generatorKey = key;
    return payload;
};
